import { useState, useEffect, useRef } from "react";
import { inventoryTransactionApi, inventorySummaryApi, productApi, inventoryItemDtlApi } from "../api/api";
import "./common.css";
import { useResizableColumns } from "../hooks/useResizableColumns";
import { usePagination }       from "../hooks/usePagination";
import Pagination              from "../components/Pagination";

/* ── Transaction type config ── */
const TRANSACTION_TYPES = [
  { value: "Stock In",   label: "Stock In",   color: "in"  },
  { value: "Stock Out",  label: "Stock Out",  color: "out" },
  { value: "Adjustment", label: "Adjustment", color: "adj" },
  { value: "Damage",     label: "Damage",     color: "dmg" },
];

/* ── Toast ── */
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3200);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className={`rv-toast rv-toast--${type}`}>
      <span className="rv-toast-icon">{type === "success" ? <CheckIcon /> : <AlertIcon />}</span>
      <span className="rv-toast-msg">{message}</span>
      <button className="rv-toast-close" onClick={onClose}><CloseIcon /></button>
    </div>
  );
}

/* ── Confirm Dialog ── */
function ConfirmDialog({ name, onConfirm, onCancel }) {
  return (
    <div className="rv-dialog-backdrop">
      <div className="rv-dialog">
        <div className="rv-dialog-icon"><TrashIcon /></div>
        <h3 className="rv-dialog-title">Delete Transaction?</h3>
        <p className="rv-dialog-desc">
          <strong>{name}</strong> will be permanently removed. This action cannot be undone.
        </p>
        <div className="rv-dialog-actions">
          <button className="rv-btn rv-btn--ghost" onClick={onCancel}>Cancel</button>
          <button className="rv-btn rv-btn--danger" onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </div>
  );
}

/* ── Transaction Detail Drawer ── */
function TransactionDrawer({ txn, getProductName, getStockLabel, getItemLabel, onClose, onEdit }) {
  const typeConf = TRANSACTION_TYPES.find(t => t.value === txn.transactionType) ?? {};
  return (
    <div className="rv-dialog-backdrop" onClick={onClose}>
      <div className="rv-txn-drawer" onClick={e => e.stopPropagation()}>
        <div className="rv-txn-drawer-header">
          <div className={`rv-txn-drawer-avatar rv-txn-avatar--${typeConf.color ?? "undefined"}`}>
            <TxnIcon type={typeConf.color} />
          </div>
          <div className="rv-txn-drawer-title-wrap">
            <h3 className="rv-txn-drawer-name">Transaction #{txn.transactionID}</h3>
            <span className={`rv-txn-type-pill rv-txn-type-pill--${typeConf.color ?? "undefined"}`}>
              {txn.transactionType || "—"}
            </span>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>

        <div className="rv-txn-drawer-body">
          <div className="rv-txn-detail-grid">
            <div className="rv-txn-detail-item">
              <span className="rv-txn-detail-label"><ProductIcon /> Product</span>
              <span className="rv-txn-detail-val">{getProductName(txn.productID)}</span>
            </div>
            <div className="rv-txn-detail-item">
              <span className="rv-txn-detail-label"><QtyIcon /> Quantity</span>
              <span className="rv-txn-detail-val rv-txn-detail-qty">{txn.quantity ?? "—"}</span>
            </div>
            <div className="rv-txn-detail-item">
              <span className="rv-txn-detail-label"><ItemIcon /> Inventory Item</span>
              <span className="rv-txn-detail-val">{getItemLabel(txn.inventoryItemID)}</span>
            </div>
            <div className="rv-txn-detail-item">
              <span className="rv-txn-detail-label"><StockIcon /> Stock Entry</span>
              <span className="rv-txn-detail-val">{getStockLabel(txn.stockID)}</span>
            </div>
            {txn.comments && (
              <div className="rv-txn-detail-item rv-txn-detail-item--full">
                <span className="rv-txn-detail-label"><CommentIcon /> Comments</span>
                <span className="rv-txn-detail-val rv-txn-detail-comment">{txn.comments}</span>
              </div>
            )}
            {txn.lastUPDTTM && (
              <div className="rv-txn-detail-item rv-txn-detail-item--full">
                <span className="rv-txn-detail-label"><ClockIcon /> Last Updated</span>
                <span className="rv-txn-detail-val">
                  {new Date(txn.lastUPDTTM).toLocaleString("en-IN", {
                    day: "2-digit", month: "short", year: "numeric",
                    hour: "2-digit", minute: "2-digit",
                  })}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="rv-txn-drawer-footer">
          <button className="rv-btn rv-btn--ghost" onClick={onClose}>Close</button>
          <button className="rv-btn rv-btn--primary" onClick={() => { onEdit(txn); onClose(); }}>
            <EditIcon /> Edit Transaction
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Transaction Form Modal ── */
function TransactionModal({ initial, onSave, onClose, saving, products, stockEntries, inventoryItems }) {
  const isEdit = !!initial?.transactionID;
  const [form, setForm] = useState({
    inventoryItemID: initial?.inventoryItemID ?? "",
    stockID:         initial?.stockID         ?? "",
    productID:       initial?.productID       ?? "",
    transactionType: initial?.transactionType ?? "",
    quantity:        initial?.quantity        ?? "",
    comments:        initial?.comments        ?? "",
  });
  const [errors, setErrors] = useState({});

  const set = (key, val) => {
    setForm(f => ({ ...f, [key]: val }));
    setErrors(e => ({ ...e, [key]: "" }));
  };

  /* When inventory item is selected, auto-fill product & stock if available */
  const handleItemSelect = (itemID) => {
    set("inventoryItemID", Number(itemID) || "");
    if (itemID) {
      const item = inventoryItems.find(i => i.inventoryItemID === Number(itemID));
      if (item) {
        if (item.productID) setForm(f => ({ ...f, inventoryItemID: Number(itemID), productID: item.productID, stockID: item.stockID || f.stockID }));
      }
    }
  };

  const validate = () => {
    const e = {};
    if (!form.productID)       e.productID       = "Product is required.";
    if (!form.transactionType) e.transactionType = "Transaction type is required.";
    if (form.quantity === "" || form.quantity === null)
                               e.quantity        = "Quantity is required.";
    if (Number(form.quantity) < 0) e.quantity    = "Quantity cannot be negative.";
    return e;
  };

  const handleSubmit = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    onSave({ ...initial, ...form });
  };

  return (
    <div className="rv-dialog-backdrop">
      <div className="rv-dialog rv-dialog--form rv-txn-modal">
        <div className="rv-modal-header">
          <div className="rv-modal-icon"><TxnIcon /></div>
          <div>
            <h3 className="rv-modal-title">{isEdit ? "Edit Transaction" : "New Transaction"}</h3>
            <p className="rv-modal-sub">{isEdit ? "Update the transaction details." : "Record a new inventory movement."}</p>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>

        <div className="rv-modal-body">

          {/* Product */}
          <div className="rv-field">
            <label className="rv-label">Product <span className="rv-required">*</span></label>
            <div className="rv-select-wrap">
              <select
                className={`rv-select ${errors.productID ? "rv-input--err" : ""}`}
                value={form.productID}
                onChange={e => set("productID", Number(e.target.value))}
              >
                <option value="">Select product…</option>
                {products.map(p => (
                  <option key={p.productID} value={p.productID}>{p.productName}</option>
                ))}
              </select>
              <span className="rv-select-arrow"><ChevronIcon /></span>
            </div>
            {errors.productID && <p className="rv-field-err">{errors.productID}</p>}
          </div>

          {/* Transaction Type + Quantity — 2 col */}
          <div className="rv-field-row-2col">
            <div className="rv-field">
              <label className="rv-label">Transaction Type <span className="rv-required">*</span></label>
              <div className="rv-select-wrap">
                <select
                  className={`rv-select ${errors.transactionType ? "rv-input--err" : ""}`}
                  value={form.transactionType}
                  onChange={e => set("transactionType", e.target.value)}
                >
                  <option value="">Select type…</option>
                  {TRANSACTION_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
                <span className="rv-select-arrow"><ChevronIcon /></span>
              </div>
              {errors.transactionType && <p className="rv-field-err">{errors.transactionType}</p>}
            </div>

            <div className="rv-field">
              <label className="rv-label">Quantity <span className="rv-required">*</span></label>
              <input
                type="number"
                min="0"
                className={`rv-input ${errors.quantity ? "rv-input--err" : ""}`}
                placeholder="e.g. 10"
                value={form.quantity}
                onChange={e => set("quantity", e.target.value)}
              />
              {errors.quantity && <p className="rv-field-err">{errors.quantity}</p>}
            </div>
          </div>

          {/* Inventory Item */}
          <div className="rv-field">
            <label className="rv-label">Inventory Item</label>
            <div className="rv-select-wrap">
              <select
                className="rv-select"
                value={form.inventoryItemID}
                onChange={e => handleItemSelect(e.target.value)}
              >
                <option value="">Select inventory item…</option>
                {inventoryItems.map(item => (
                  <option key={item.inventoryItemID} value={item.inventoryItemID}>
                    Item #{item.inventoryItemID}
                    {item.productID ? ` — Prod #${item.productID}` : ""}
                    {item.quantity != null ? ` (Qty: ${item.quantity})` : ""}
                  </option>
                ))}
              </select>
              <span className="rv-select-arrow"><ChevronIcon /></span>
            </div>
            <p className="rv-field-hint">Selecting an item auto-fills Product & Stock below.</p>
          </div>

          {/* Stock Entry */}
          <div className="rv-field">
            <label className="rv-label">Stock Entry</label>
            <div className="rv-select-wrap">
              <select
                className="rv-select"
                value={form.stockID}
                onChange={e => set("stockID", Number(e.target.value))}
              >
                <option value="">Select stock entry…</option>
                {stockEntries.map(s => (
                  <option key={s.stockID} value={s.stockID}>
                    Stock #{s.stockID}{s.productID ? ` — Prod #${s.productID}` : ""}{s.quantity != null ? ` (Qty: ${s.quantity})` : ""}
                  </option>
                ))}
              </select>
              <span className="rv-select-arrow"><ChevronIcon /></span>
            </div>
          </div>

          {/* Comments */}
          <div className="rv-field">
            <label className="rv-label">Comments</label>
            <textarea
              className="rv-input rv-textarea"
              placeholder="Optional notes about this transaction…"
              rows={3}
              value={form.comments}
              onChange={e => set("comments", e.target.value)}
            />
          </div>

        </div>

        <div className="rv-modal-footer">
          <button className="rv-btn rv-btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="rv-btn rv-btn--primary" onClick={handleSubmit} disabled={saving}>
            {saving ? <span className="rv-spinner" /> : null}
            {isEdit ? "Update" : "Record"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   MAIN INVENTORY TRANSACTION PAGE
══════════════════════════════════════ */
export default function InventoryTransactionPage() {
  const [transactions,  setTransactions]  = useState([]);
  const [products,      setProducts]      = useState([]);
  const [stockEntries,  setStockEntries]  = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading,       setLoading]       = useState(true);

  const [search,     setSearch]     = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterProd, setFilterProd] = useState("all");

  const [modal,   setModal]   = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [drawer,  setDrawer]  = useState(null);
  const [toast,   setToast]   = useState(null);

  /* ── Resizable columns ── */
  const tableRef = useRef(null);
  const [tableReady, setTableReady] = useState(false);
  useResizableColumns(tableRef, tableReady, [50, 120, 200, 110, 110, 80, 180, 130, 100]);

  /* ── Fetch all ── */
  const fetchAll = async () => {
    try {
      setLoading(true);
      setTableReady(false);
      const [txnData, prodData, stockData, itemData] = await Promise.all([
        inventoryTransactionApi.getAll(),
        productApi.getAll(),
        inventorySummaryApi.getAll(),
        inventoryItemDtlApi.getAll(),
      ]);
      setTransactions( Array.isArray(txnData)   ? txnData   : txnData?.data   ?? []);
      setProducts(     Array.isArray(prodData)   ? prodData  : prodData?.data  ?? []);
      setStockEntries( Array.isArray(stockData)  ? stockData : stockData?.data ?? []);
      setInventoryItems(Array.isArray(itemData)  ? itemData  : itemData?.data  ?? []);
    } catch (err) {
      showToast(err.message || "Failed to load data.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { if (!loading) setTableReady(true); }, [loading]);

  const showToast = (message, type = "success") => setToast({ message, type });

  /* ── Lookup helpers ── */
  const getProductName = id => products.find(p => p.productID === id)?.productName ?? (id ? `Prod #${id}` : "—");
  const getStockLabel  = id => id ? `Stock #${id}` : "—";
  const getItemLabel   = id => id ? `Item #${id}` : "—";

  /* ── Stats ── */
  const statCounts = TRANSACTION_TYPES.reduce((acc, t) => {
    acc[t.value] = transactions.filter(tx => tx.transactionType === t.value).length;
    return acc;
  }, {});

  /* ── Product options for filter ── */
  const prodOptions = Array.from(
    new Map(transactions.map(tx => [tx.productID, getProductName(tx.productID)])).entries()
  );

  /* ── Save ── */
  const handleSave = async (formData) => {
    setSaving(true);
    try {
      if (formData.transactionID) {
        await inventoryTransactionApi.update(formData);
        showToast("Transaction updated successfully.");
      } else {
        await inventoryTransactionApi.create(formData);
        showToast("Transaction recorded successfully.");
      }
      setModal(null);
      fetchAll();
    } catch (err) {
      showToast(err.message || "Something went wrong.", "error");
    } finally {
      setSaving(false);
    }
  };

  /* ── Delete ── */
  const handleDelete = async () => {
    if (!confirm) return;
    try {
      await inventoryTransactionApi.remove(confirm.id);
      showToast("Transaction deleted.");
      setConfirm(null);
      fetchAll();
    } catch (err) {
      showToast(err.message || "Delete failed.", "error");
    }
  };

  /* ── Filtered list ── */
  const filtered = transactions.filter(tx => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      getProductName(tx.productID).toLowerCase().includes(q) ||
      tx.transactionType?.toLowerCase().includes(q) ||
      tx.comments?.toLowerCase().includes(q) ||
      String(tx.transactionID).includes(q) ||
      String(tx.inventoryItemID).includes(q);
    const matchType = filterType === "all" || tx.transactionType === filterType;
    const matchProd = filterProd === "all" || tx.productID === Number(filterProd);
    return matchSearch && matchType && matchProd;
  });

  /* ── Pagination ── */
  const {
    paged, page, totalPages, pageSize,
    setPage, setPageSize,
    pageStart, pageEnd, totalItems,
  } = usePagination(filtered, 10);

  /* ── Format date ── */
  const fmtDate = iso => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  return (
    <div className="rv-txn-page">

      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
      {confirm && (
        <ConfirmDialog
          name={`Transaction #${confirm.id}`}
          onConfirm={handleDelete}
          onCancel={() => setConfirm(null)}
        />
      )}
      {modal && (
        <TransactionModal
          initial={modal.data}
          onSave={handleSave}
          onClose={() => setModal(null)}
          saving={saving}
          products={products}
          stockEntries={stockEntries}
          inventoryItems={inventoryItems}
        />
      )}
      {drawer && (
        <TransactionDrawer
          txn={drawer}
          getProductName={getProductName}
          getStockLabel={getStockLabel}
          getItemLabel={getItemLabel}
          onClose={() => setDrawer(null)}
          onEdit={tx => setModal({ data: tx })}
        />
      )}

      {/* ── Topbar ── */}
      <div className="rv-txn-topbar">
        <div className="rv-txn-topbar-left">
          <h2 className="rv-txn-heading">Inventory Transactions</h2>
          <span className="rv-txn-count">{transactions.length} total</span>
        </div>
        <button className="rv-btn rv-btn--primary" onClick={() => setModal({ data: {} })}>
          <PlusIcon /> New Transaction
        </button>
      </div>

      {/* ── Stat pills ── */}
      {!loading && transactions.length > 0 && (
        <div className="rv-txn-stats">
          {TRANSACTION_TYPES.map(t => (
            <div key={t.value} className="rv-txn-stat-pill">
              <div className={`rv-txn-stat-icon rv-txn-stat-icon--${t.color}`}>
                <TxnIcon type={t.color} />
              </div>
              <div>
                <p className="rv-txn-stat-label">{t.label}</p>
                <p className="rv-txn-stat-val">{statCounts[t.value] ?? 0}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Filters ── */}
      <div className="rv-txn-filters">
        <div className="rv-txn-search">
          <SearchIcon />
          <input
            placeholder="Search by product, type, item ID, comments…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          {search && (
            <button className="rv-txn-search-clear" onClick={() => { setSearch(""); setPage(1); }}>
              <CloseIcon />
            </button>
          )}
        </div>

        <div className="rv-select-wrap rv-txn-prod-filter">
          <select
            className="rv-select"
            value={filterProd}
            onChange={e => { setFilterProd(e.target.value); setPage(1); }}
          >
            <option value="all">All Products</option>
            {prodOptions.map(([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </select>
          <span className="rv-select-arrow"><ChevronIcon /></span>
        </div>

        <div className="rv-filter-tabs">
          <button
            className={`rv-filter-tab ${filterType === "all" ? "rv-filter-tab--active" : ""}`}
            onClick={() => { setFilterType("all"); setPage(1); }}
          >All</button>
          {TRANSACTION_TYPES.map(t => (
            <button
              key={t.value}
              className={`rv-filter-tab rv-filter-tab--txn ${filterType === t.value ? "rv-filter-tab--active" : ""}`}
              onClick={() => { setFilterType(t.value); setPage(1); }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Table card ── */}
      <div className="rv-card rv-txn-card">
        {loading ? (
          <div className="rv-txn-empty">
            <div className="rv-loader" />
            <p>Loading transactions…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="rv-txn-empty">
            <div className="rv-empty-icon"><TxnIcon /></div>
            <p className="rv-empty-title">No transactions found</p>
            <p className="rv-empty-sub">
              {search ? "Try a different search term." : "Record your first inventory transaction."}
            </p>
            {!search && (
              <button className="rv-btn rv-btn--primary rv-mt-12" onClick={() => setModal({ data: {} })}>
                <PlusIcon /> New Transaction
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="rv-table-wrap rv-txn-table-wrap">
              <table className="rv-table rv-txn-table" ref={tableRef}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Type</th>
                    <th>Product</th>
                    <th>Item ID</th>
                    <th>Stock ID</th>
                    <th>Qty</th>
                    <th>Comments</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((tx, i) => {
                    const typeConf = TRANSACTION_TYPES.find(t => t.value === tx.transactionType) ?? {};
                    return (
                      <tr
                        key={tx.transactionID}
                        className="rv-txn-row"
                        onClick={() => setDrawer(tx)}
                        style={{ cursor: "pointer" }}
                      >
                        <td className="rv-txn-idx">{pageStart + i}</td>

                        {/* Type */}
                        <td>
                          <span className={`rv-txn-type-pill rv-txn-type-pill--${typeConf.color ?? "undefined"}`}>
                            {tx.transactionType || "—"}
                          </span>
                        </td>

                        {/* Product */}
                        <td>
                          <div className="rv-txn-prod-cell">
                            <span className="rv-txn-prod-name">{getProductName(tx.productID)}</span>
                          </div>
                        </td>

                        {/* Inventory Item ID */}
                        <td>
                          {tx.inventoryItemID
                            ? <span className="rv-txn-id-badge">#{tx.inventoryItemID}</span>
                            : <span className="rv-empty-val">—</span>}
                        </td>

                        {/* Stock ID */}
                        <td>
                          {tx.stockID
                            ? <span className="rv-txn-id-badge">#{tx.stockID}</span>
                            : <span className="rv-empty-val">—</span>}
                        </td>

                        {/* Qty */}
                        <td>
                          <span className={`rv-txn-qty rv-txn-qty--${typeConf.color ?? "undefined"}`}>
                            {typeConf.color === "out" || typeConf.color === "dmg" ? "−" : "+"}
                            {tx.quantity ?? 0}
                          </span>
                        </td>

                        {/* Comments */}
                        <td>
                          <span className="rv-txn-comment-cell">
                            {tx.comments || <span className="rv-empty-val">—</span>}
                          </span>
                        </td>

                        {/* Date */}
                        <td>
                          <span className="rv-txn-date">{fmtDate(tx.lastUPDTTM)}</span>
                        </td>

                        {/* Actions */}
                        <td onClick={e => e.stopPropagation()}>
                          <div className="rv-actions">
                            <button
                              className="rv-action-btn rv-action-btn--edit"
                              title="Edit"
                              onClick={() => setModal({ data: tx })}
                            >
                              <EditIcon />
                            </button>
                            <button
                              className="rv-action-btn rv-action-btn--del"
                              title="Delete"
                              onClick={() => setConfirm({ id: tx.transactionID })}
                            >
                              <TrashIcon />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <Pagination
              page={page}
              totalPages={totalPages}
              pageSize={pageSize}
              setPage={setPage}
              setPageSize={setPageSize}
              pageStart={pageStart}
              pageEnd={pageEnd}
              totalItems={totalItems}
            />
          </>
        )}
      </div>

      {/* ── Resize hint ── */}
      {!loading && filtered.length > 0 && (
        <p className="rv-resize-hint">
          <GripIcon /> Drag column borders to resize · Click a row to view details
        </p>
      )}
    </div>
  );
}

/* ── TxnIcon — changes per type ── */
function TxnIcon({ type }) {
  if (type === "in")  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>;
  if (type === "out") return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/></svg>;
  if (type === "adj") return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>;
  if (type === "dmg") return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polygon points="7.86 2 16.14 2 22 7.86 22 16.14 16.14 22 7.86 22 2 16.14 2 7.86 7.86 2"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>;
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="2" y="3" width="20" height="14" rx="2"/><polyline points="8 21 12 17 16 21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>;
}

function PlusIcon()    { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }
function SearchIcon()  { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>; }
function EditIcon()    { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>; }
function TrashIcon()   { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>; }
function CloseIcon()   { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>; }
function CheckIcon()   { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>; }
function AlertIcon()   { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>; }
function ChevronIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>; }
function GripIcon()    { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" style={{width:13,height:13}}><circle cx="9" cy="5" r="1" fill="currentColor"/><circle cx="9" cy="12" r="1" fill="currentColor"/><circle cx="9" cy="19" r="1" fill="currentColor"/><circle cx="15" cy="5" r="1" fill="currentColor"/><circle cx="15" cy="12" r="1" fill="currentColor"/><circle cx="15" cy="19" r="1" fill="currentColor"/></svg>; }
function ProductIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><line x1="16.5" y1="9.4" x2="7.5" y2="4.21"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>; }
function QtyIcon()     { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>; }
function StockIcon()   { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M5 8h14M5 8a2 2 0 1 0-4 0 2 2 0 0 0 4 0zM5 8v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8m-14 0a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2"/></svg>; }
function ItemIcon()    { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>; }
function CommentIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>; }
function ClockIcon()   { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>; }